import React from 'react';
import { ChartBarIcon } from '@heroicons/react/24/solid';
import { useAnalyticsSections } from './useAnalyticsSections';

function AnalyticsTablesLoader() {
  return (
    <div className="space-y-8">
      {Array.from({ length: 4 }).map((_, sectionIndex) => (
        <div key={sectionIndex} className="w-full overflow-hidden rounded-[2rem] border border-slate-200 bg-white px-5 py-8 shadow-sm shadow-slate-200/60 sm:px-6">
          <div className="mb-6 flex items-center gap-2">
            <div className="h-6 w-6 animate-pulse rounded bg-slate-200" />
            <div className="h-6 w-48 animate-pulse rounded bg-slate-200" />
          </div>
          <div className="overflow-x-auto">
            <div className="min-w-full space-y-3">
              <div className="h-10 animate-pulse rounded bg-slate-100" />
              {Array.from({ length: 4 }).map((__, rowIndex) => (
                <div key={rowIndex} className="h-12 animate-pulse rounded bg-slate-50" />
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function formatValue(value, mode) {
  if (value === 0 || value === undefined) {
    return '—';
  }

  if (mode === 'revenue') {
    return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  return value;
}

function AnalyticsTableSection({ title, primaryHeader, secondaryHeader, rows, loading, mode, states }) {
  const skeletonCount = rows.length || (title === 'Monthly Analytics' ? 8 : title === 'Weekly Analytics' ? 5 : title === 'Daily Analytics' ? 7 : 4);

  return (
    <div className="mb-12 w-full overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm shadow-slate-200/60 sm:p-8">
      <h2 className="mb-6 flex items-center gap-2 text-md font-medium uppercase tracking-[0.08em] text-gray-800">
        <ChartBarIcon className="h-6 w-6 text-blue-600" />
        {title}
      </h2>
      <div className="overflow-hidden rounded-[1.5rem] border border-slate-200 bg-white shadow-sm shadow-slate-200/50">
        <div className="overflow-x-auto">
        <table className="min-w-full text-center">
          <thead className="bg-slate-50/80">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-700">{primaryHeader}</th>
              {secondaryHeader && (
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-700">{secondaryHeader}</th>
              )}
              {states.map((state) => (
                <th key={state} className="px-4 py-4 text-xs font-bold uppercase tracking-wider text-slate-700">{state}</th>
              ))}
              <th className="px-4 py-4 text-xs font-bold uppercase tracking-wider text-slate-700">Total</th>
              <th className="px-4 py-4 text-xs font-bold uppercase tracking-wider text-slate-700">Paid</th>
              <th className="px-4 py-4 text-xs font-bold uppercase tracking-wider text-slate-700">Unpaid</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 bg-white">
            {loading
              ? Array.from({ length: skeletonCount }).map((_, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 text-left font-medium text-gray-900 animate-pulse">...</td>
                    {secondaryHeader && <td className="px-6 py-4 text-left text-gray-700 animate-pulse">...</td>}
                    {states.map((state) => (
                      <td key={state} className="px-4 py-4 text-gray-700 animate-pulse">...</td>
                    ))}
                    <td className="px-4 py-4 text-gray-700 animate-pulse">...</td>
                    <td className="px-4 py-4 text-gray-700 animate-pulse">...</td>
                    <td className="px-4 py-4 text-gray-700 animate-pulse">...</td>
                  </tr>
                ))
              : rows.map((row) => (
                  <tr key={`${title}-${row.label}`}>
                    <td className="px-6 py-4 font-medium text-left text-gray-900">{row.label}</td>
                    {secondaryHeader && <td className="px-6 py-4 text-left text-gray-700">{row.secondaryLabel}</td>}
                    {states.map((state) => (
                      <td key={state} className="px-4 py-4 text-gray-700">{formatValue(row[state], mode)}</td>
                    ))}
                    <td className="px-4 py-4 text-blue-600 font-bold">{formatValue(row.total, mode)}</td>
                    <td className="px-4 py-4 text-green-600 font-bold">{formatValue(row.paid, mode)}</td>
                    <td className="px-4 py-4 text-red-600 font-bold">{formatValue(row.unpaid, mode)}</td>
                  </tr>
                ))}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}

export default function AnalysisTablesShared({ mode }) {
  const { sections, loading, states } = useAnalyticsSections(mode);

  if (loading && sections.length === 0) {
    return <AnalyticsTablesLoader />;
  }

  return sections.map((section) => (
    <AnalyticsTableSection
      key={section.title}
      title={section.title}
      primaryHeader={section.primaryHeader}
      secondaryHeader={section.secondaryHeader}
      rows={section.rows}
      loading={loading}
      mode={mode}
      states={states}
    />
  ));
}